import { Add as AddIcon, QrCodeScanner, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AssetCard, QRScanner } from '../components';
import { supabase } from '../lib/supabase';
import type { Asset, Customer } from '../types';

export function Assets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [formData, setFormData] = useState<{
    customer_id: string;
    serial_number: string;
    type: string;
    status: 'active' | 'maintenance' | 'retired';
  }>({
    customer_id: '',
    serial_number: '',
    type: '',
    status: 'active',
  });

  async function fetchAssets() {
    try {
      const { data, error } = await supabase
        .from('assets')
        .select(
          `
          id, customer_id, tag_id, serial_number, type, status, created_at, updated_at,
          customer:customers(id, name)
        `
        )
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssets((data as Asset[]) || []);
    } catch (error) {
      console.error('Error fetching assets:', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchCustomers() {
    try {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name, email, phone, address, created_at, updated_at')
        .order('name');

      if (error) throw error;
      setCustomers((data as Customer[]) || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  }

  useEffect(() => {
    fetchAssets();
    fetchCustomers();
  }, []);

  const filteredAssets = assets.filter(asset => {
    const matchesSearch =
      asset.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
      asset.tag_id.toLowerCase().includes(search.toLowerCase()) ||
      asset.type?.toLowerCase().includes(search.toLowerCase());

    const matchesStatus = statusFilter === 'all' || asset.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const handleOpenDialog = () => {
    setFormData({
      customer_id: '',
      serial_number: '',
      type: '',
      status: 'active',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleSubmit = async () => {
    try {
      const tag_id = crypto.randomUUID();

      const { error } = await supabase.from('assets').insert({
        customer_id: formData.customer_id,
        serial_number: formData.serial_number || null,
        type: formData.type || null,
        status: formData.status,
        tag_id,
      });

      if (error) throw error;

      handleCloseDialog();
      fetchAssets();
    } catch (error) {
      console.error('Error creating asset:', error);
    }
  };

  const handleScan = (result: string) => {
    setScannerOpen(false);
    // Navigate to public asset view (works with tag_id from QR code)
    navigate(`/asset/${result}`);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" component="h1" fontWeight={600}>
          Assets
        </Typography>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<QrCodeScanner sx={{ display: { xs: 'none', sm: 'inline' } }} />}
            onClick={() => setScannerOpen(true)}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Scan QR</Box>
            <QrCodeScanner sx={{ display: { xs: 'inline', sm: 'none' } }} />
          </Button>
          <Button 
            variant="contained" 
            startIcon={<AddIcon sx={{ display: { xs: 'none', sm: 'inline' } }} />} 
            onClick={handleOpenDialog}
            size="small"
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Add Asset</Box>
            <AddIcon sx={{ display: { xs: 'inline', sm: 'none' } }} />
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 8 }}>
              <TextField
                placeholder="Search assets..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={e => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="maintenance">Maintenance</MenuItem>
                  <MenuItem value="retired">Retired</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
              <Card>
                <CardContent>
                  <Skeleton variant="rectangular" height={80} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : filteredAssets.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary">
                  No assets found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {search
                    ? 'Try adjusting your search terms'
                    : 'Create your first asset to get started'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ) : (
          filteredAssets.map(asset => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={asset.id}>
              <AssetCard asset={asset} onClick={() => navigate(`/assets/${asset.id}`)} />
            </Grid>
          ))
        )}
      </Grid>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>Customer</InputLabel>
              <Select
                value={formData.customer_id}
                label="Customer"
                onChange={e => setFormData({ ...formData, customer_id: e.target.value })}
              >
                {customers.map(customer => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Serial Number"
              value={formData.serial_number}
              onChange={e => setFormData({ ...formData, serial_number: e.target.value })}
              fullWidth
            />
            <TextField
              label="Type"
              value={formData.type}
              onChange={e => setFormData({ ...formData, type: e.target.value })}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={e =>
                  setFormData({
                    ...formData,
                    status: e.target.value as 'active' | 'retired' | 'maintenance',
                  })
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={!formData.customer_id}>
            Add Asset
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={scannerOpen} onClose={() => setScannerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Scan QR Code</DialogTitle>
        <DialogContent>
          <QRScanner onScan={handleScan} onClose={() => setScannerOpen(false)} />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
